import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Stack } from '@mui/material';

import type { OpportunityDeal } from '~/hooks/apis/offers/useGetOffer';
import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import ApprovalQueue from './ApprovalQueue';
import ChecklistToolbar from './ChecklistToolbar';
import ContextCard from './ContextCard';
import PlaceholderChecklistTab from './PlaceholderChecklistTab';
import { CM_CHECKLIST_CATEGORIES } from './constants';
import {
  BlackoutReportCard,
  BundledExperiencesReportCard,
  CampaignReportCard,
  CapacityReportCard,
  DatesRestrictionsReportCard,
  MarginReportCard,
  SetupReportCard,
  SurchargesReportCard,
  TaxesPaymentsReportCard,
} from './reportCards';
import type { SetupChecklistRow, SetupRuleInput } from './setupRules';
import { SETUP_EVIDENCE_LABELS, buildSetupChecklistRules } from './setupRules';
import type {
  ApprovalQueueItem,
  ApprovalReportSummary,
  ChecklistCategory,
  ChecklistCategoryId,
  ChecklistRow,
  ChecklistTabId,
  EditedBlackoutValues,
  EditedBundledExperienceValues,
  EditedCapacityValues,
  EditedMarginValues,
  ManualApprovalState,
  SetupInputOverrides,
} from './types';
import type {
  BlackoutChecklistRow,
  BlackoutCoverageSummary,
  BlackoutDateRange,
  BundledExperienceChecklistRow,
  CampaignChecklistRow,
  ChecklistStatus,
  DatesRestrictionsChecklistRow,
  EgsCapacityChecklistRow,
  EgsMarginChecklistRow,
  SurchargeTableRow,
  SurchargesChecklistRow,
  TaxesPaymentsChecklistRow,
} from './utils';
import {
  buildBlackoutRows,
  buildBundledExperienceRows,
  buildCampaignRows,
  buildDatesRestrictionsRows,
  buildEgsCapacityRows,
  buildEgsMarginRows,
  buildSurchargeTableRows,
  buildSurchargesRows,
  buildTaxesPaymentsRows,
  calculateBlackoutCoverage,
  calculateMarginPercentage,
  evaluateBlackoutRow,
  formatContextDate,
  formatMoney,
  formatOptionalNumber,
  getMarginBand,
  getMarginStatus,
  getStatusLabel,
  isBundledBlackoutOk,
  isBundledChildAgeOk,
  isBundledValidityOk,
  isFiniteNumber,
} from './utils';

const CAMPAIGN_CATEGORY = getChecklistCategory('campaign');
const DATES_RESTRICTIONS_CATEGORY = getChecklistCategory('datesRestrictions');
const EGS_CATEGORY = getChecklistCategory('egs');
const SETUP_CATEGORY = getChecklistCategory('setup');
const SURCHARGES_CATEGORY = getChecklistCategory('surcharges');
const TAXES_PAYMENTS_CATEGORY = getChecklistCategory('taxesPayments');
const BLACKOUT_CATEGORY = getChecklistCategory('blackoutDates');
const BUNDLED_EXPERIENCES_CATEGORY = getChecklistCategory('bundledExperiences');

interface Props {
  blackoutDates?: Array<BlackoutDateRange>;
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  isLoadingProperty: boolean;
  isLoadingSetup: boolean;
  isLoadingSurcharges: boolean;
  offer?: App.Offer;
  offerId: string;
  opportunity?: App.OpportunityPage;
  opportunityContract?: App.Contract;
  opportunityDeals?: Array<OpportunityDeal>;
  opportunityError?: unknown;
  packages?: Array<App.Package>;
  propertyId?: string;
  propertyError?: unknown;
  propertyHasTaxesPayableAtProperty: boolean;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  setupInput: SetupRuleInput;
  surcharges: Array<SalesforceSurchargePeriod>;
  surchargesError?: unknown;
  travelFromDate?: string;
  travelToDate?: string;
  vendorId?: string;
}

export default function CMChecklistWorkspace({
  blackoutDates,
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  isLoadingProperty,
  isLoadingSetup,
  isLoadingSurcharges,
  offer,
  offerId,
  opportunity,
  opportunityContract,
  opportunityDeals,
  opportunityError,
  packages,
  propertyId,
  propertyError,
  propertyHasTaxesPayableAtProperty,
  propertyRoomTypes,
  relatedDeals,
  relatedExtraGuestSurcharges,
  setupInput,
  surcharges,
  surchargesError,
  travelFromDate,
  travelToDate,
  vendorId,
}: Props) {
  const [showAutoApproved, setShowAutoApproved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState<ChecklistTabId>('approvalQueue');
  const [expandedQueueItems, setExpandedQueueItems] = useState<Record<string, boolean>>({});
  const [setupInputOverrides, setSetupInputOverrides] = useState<SetupInputOverrides>({});
  const [debouncedSetupInputOverrides, setDebouncedSetupInputOverrides] = useState<SetupInputOverrides>({});
  const [editedBlackouts, setEditedBlackouts] = useState<EditedBlackoutValues>({});
  const [editedMargins, setEditedMargins] = useState<EditedMarginValues>({});
  const [editedCapacities, setEditedCapacities] = useState<EditedCapacityValues>({});
  const [editedBundledExperiences, setEditedBundledExperiences] = useState<EditedBundledExperienceValues>({});
  const [manualApprovals, setManualApprovals] = useState<ManualApprovalState>({});
  const opportunityTravelFrom = opportunity?.key_dates?.expiry_date_travel_from__c ?? travelFromDate;
  const opportunityTravelTo = opportunity?.key_dates?.travel_to__c ?? travelToDate;
  const opportunityBlackoutDates = opportunity?.black_out_dates;
  const dealCurrency = getOfferCurrency(packages);
  const baseBlackoutRows = useMemo(
    () =>
      buildBlackoutRows({
        blackOutDates: opportunity?.black_out_dates,
        hasSourceError: !!opportunityError,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [opportunity?.black_out_dates, opportunityError, opportunityTravelFrom, opportunityTravelTo],
  );

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSetupInputOverrides(setupInputOverrides);
    }, 400);

    return () => window.clearTimeout(debounceTimer);
  }, [setupInputOverrides]);

  const setupRows = useMemo(
    () => buildSetupChecklistRules(applySetupInputOverrides(setupInput, debouncedSetupInputOverrides)),
    [debouncedSetupInputOverrides, setupInput],
  );
  const baseMarginRows = useMemo(
    () => buildEgsMarginRows(surcharges, relatedExtraGuestSurcharges),
    [relatedExtraGuestSurcharges, surcharges],
  );
  const baseCapacityRows = useMemo(
    () => buildEgsCapacityRows({ packages, propertyRoomTypes, surcharges }),
    [packages, propertyRoomTypes, surcharges],
  );
  const baseSurchargesRows = useMemo(
    () => buildSurchargesRows({ surcharges, travelFromDate, travelToDate }),
    [surcharges, travelFromDate, travelToDate],
  );
  const surchargeTableRows = useMemo(
    () => buildSurchargeTableRows({ blackoutDates, packages, surcharges, travelFromDate, travelToDate }),
    [blackoutDates, packages, surcharges, travelFromDate, travelToDate],
  );
  const propertyDataAvailable = propertyRoomTypes !== undefined;
  const taxesPaymentsRows = useMemo(
    () =>
      buildTaxesPaymentsRows({
        cityOtherTax: opportunityContract?.city_other_tax__c,
        cityTaxComments: opportunityContract?.city_tax_comments__c,
        hasPropertyTaxesPayableAtProperty: propertyHasTaxesPayableAtProperty,
        opportunityDataAvailable: !!opportunityContract,
        paymentMethod: opportunityContract?.payment_method__c,
        paymentMilestone: opportunityContract?.payment_milestone__c,
        prizeOrderId: opportunityContract?.prize_order_id__c,
        propertyDataAvailable,
      }),
    [opportunityContract, propertyHasTaxesPayableAtProperty, propertyDataAvailable],
  );
  const campaignRows = useMemo(
    () => buildCampaignRows({ offer, opportunity, opportunityDeals, setupInput }),
    [offer, opportunity, opportunityDeals, setupInput],
  );
  const datesRestrictionsRows = useMemo(
    () => buildDatesRestrictionsRows({ campaignType, opportunityDeals, packages }),
    [campaignType, opportunityDeals, packages],
  );
  const baseBundledExperienceRows = useMemo(
    () =>
      buildBundledExperienceRows({
        dealCurrency,
        hasOpportunitySourceError: !!opportunityError,
        hotelBlackoutDates: opportunityBlackoutDates,
        hotelTravelFrom: opportunityTravelFrom,
        hotelTravelTo: opportunityTravelTo,
        relatedDeals,
      }),
    [
      dealCurrency,
      opportunityBlackoutDates,
      opportunityError,
      opportunityTravelFrom,
      opportunityTravelTo,
      relatedDeals,
    ],
  );

  const marginRows = useMemo(
    () => applyEditedMarginRows(baseMarginRows, editedMargins),
    [baseMarginRows, editedMargins],
  );
  const capacityRows = useMemo(
    () => applyEditedCapacityRows(baseCapacityRows, editedCapacities),
    [baseCapacityRows, editedCapacities],
  );
  const blackoutRows = useMemo(
    () =>
      applyEditedBlackoutRows({
        editedBlackouts,
        rows: baseBlackoutRows,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [baseBlackoutRows, editedBlackouts, opportunityTravelFrom, opportunityTravelTo],
  );
  const bundledExperienceRows = useMemo(
    () =>
      applyEditedBundledExperienceRows({
        dealCurrency,
        editedBundledExperiences,
        hotelBlackoutDates: opportunityBlackoutDates,
        hotelTravelFrom: opportunityTravelFrom,
        hotelTravelTo: opportunityTravelTo,
        rows: baseBundledExperienceRows,
      }),
    [
      baseBundledExperienceRows,
      dealCurrency,
      editedBundledExperiences,
      opportunityBlackoutDates,
      opportunityTravelFrom,
      opportunityTravelTo,
    ],
  );
  const blackoutRowsById = useMemo(() => new Map(blackoutRows.map((row) => [row.id, row])), [blackoutRows]);
  const marginRowsById = useMemo(() => new Map(marginRows.map((row) => [row.id, row])), [marginRows]);
  const capacityRowsById = useMemo(() => new Map(capacityRows.map((row) => [row.id, row])), [capacityRows]);
  const bundledExperienceRowsById = useMemo(
    () => new Map(bundledExperienceRows.map((row) => [row.id, row])),
    [bundledExperienceRows],
  );
  const blackoutCoverage = useMemo(
    () =>
      calculateBlackoutCoverage({
        blackOutDates: blackoutRows,
        travelFrom: opportunityTravelFrom,
        travelTo: opportunityTravelTo,
      }),
    [blackoutRows, opportunityTravelFrom, opportunityTravelTo],
  );

  useEffect(() => {
    setSetupInputOverrides({});
    setDebouncedSetupInputOverrides({});
    setEditedBlackouts({});
    setEditedMargins({});
    setEditedCapacities({});
    setEditedBundledExperiences({});
    setManualApprovals({});
    setExpandedQueueItems({});
  }, [
    blackoutDates,
    offer,
    opportunity,
    opportunityDeals,
    opportunityError,
    packages,
    propertyRoomTypes,
    relatedDeals,
    relatedExtraGuestSurcharges,
    setupInput,
    surcharges,
    travelFromDate,
    travelToDate,
  ]);

  const isApprovalQueueMode = activeTabId === 'approvalQueue';
  const activeCategory = isApprovalQueueMode ? SETUP_CATEGORY : getChecklistCategory(activeTabId);
  const isSetupCategoryActive = activeCategory.id === 'setup';
  const isCampaignCategoryActive = activeCategory.id === 'campaign';
  const isDatesRestrictionsCategoryActive = activeCategory.id === 'datesRestrictions';
  const isEgsCategoryActive = activeCategory.id === 'egs';
  const isSurchargesCategoryActive = activeCategory.id === 'surcharges';
  const isTaxesPaymentsCategoryActive = activeCategory.id === 'taxesPayments';
  const isBlackoutCategoryActive = activeCategory.id === 'blackoutDates';
  const isBundledExperiencesCategoryActive = activeCategory.id === 'bundledExperiences';

  const visibleSetupRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isSetupCategoryActive || isApprovalQueueMode ? setupRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getSetupSearchText,
      }),
    [isApprovalQueueMode, isSetupCategoryActive, manualApprovals, searchQuery, setupRows, showAutoApproved],
  );
  const visibleCampaignRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isCampaignCategoryActive || isApprovalQueueMode ? campaignRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getCampaignSearchText,
      }),
    [campaignRows, isApprovalQueueMode, isCampaignCategoryActive, manualApprovals, searchQuery, showAutoApproved],
  );
  const visibleDatesRestrictionsRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isDatesRestrictionsCategoryActive || isApprovalQueueMode ? datesRestrictionsRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getDatesRestrictionsSearchText,
      }),
    [
      datesRestrictionsRows,
      isApprovalQueueMode,
      isDatesRestrictionsCategoryActive,
      manualApprovals,
      searchQuery,
      showAutoApproved,
    ],
  );
  const visibleMarginRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? marginRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getMarginSearchText,
      }),
    [isApprovalQueueMode, isEgsCategoryActive, manualApprovals, marginRows, searchQuery, showAutoApproved],
  );
  const visibleCapacityRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isEgsCategoryActive || isApprovalQueueMode ? capacityRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getCapacitySearchText,
      }),
    [capacityRows, isApprovalQueueMode, isEgsCategoryActive, manualApprovals, searchQuery, showAutoApproved],
  );
  const visibleSurchargeTableRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isSurchargesCategoryActive || isApprovalQueueMode ? surchargeTableRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getSurchargeTableSearchText,
      }),
    [
      isApprovalQueueMode,
      isSurchargesCategoryActive,
      manualApprovals,
      searchQuery,
      showAutoApproved,
      surchargeTableRows,
    ],
  );
  const visibleSurchargeRules = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isApprovalQueueMode ? baseSurchargesRows : [],
        searchQuery,
        showAutoApproved: false,
        text: getSurchargesSearchText,
      }),
    [baseSurchargesRows, isApprovalQueueMode, manualApprovals, searchQuery],
  );
  const visibleTaxesPaymentsRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isTaxesPaymentsCategoryActive || isApprovalQueueMode ? taxesPaymentsRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getTaxesPaymentsSearchText,
      }),
    [
      taxesPaymentsRows,
      isApprovalQueueMode,
      isTaxesPaymentsCategoryActive,
      manualApprovals,
      searchQuery,
      showAutoApproved,
    ],
  );
  const visibleBlackoutRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isBlackoutCategoryActive || isApprovalQueueMode ? blackoutRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getBlackoutSearchText,
      }),
    [blackoutRows, isApprovalQueueMode, isBlackoutCategoryActive, manualApprovals, searchQuery, showAutoApproved],
  );
  const visibleBundledExperienceRows = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isBundledExperiencesCategoryActive || isApprovalQueueMode ? bundledExperienceRows : [],
        searchQuery,
        showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
        text: getBundledExperienceSearchText,
      }),
    [
      bundledExperienceRows,
      isApprovalQueueMode,
      isBundledExperiencesCategoryActive,
      manualApprovals,
      searchQuery,
      showAutoApproved,
    ],
  );

  const visibleQueueItems = useMemo(
    () => [
      ...visibleSetupRows.map((row) => buildSetupQueueItem(row)),
      ...visibleCampaignRows.map((row) => buildCampaignQueueItem(row)),
      ...visibleDatesRestrictionsRows.map((row) => buildDatesRestrictionsQueueItem(row)),
      ...visibleBlackoutRows.map((row) => buildBlackoutQueueItem(row, blackoutCoverage)),
      ...visibleMarginRows.map((row) => buildMarginQueueItem(row)),
      ...visibleCapacityRows.map((row) => buildCapacityQueueItem(row)),
      ...visibleSurchargeTableRows.map((row) => buildSurchargeTableQueueItem(row)),
      ...visibleSurchargeRules.map((row) => buildSurchargesQueueItem(row)),
      ...visibleTaxesPaymentsRows.map((row) => buildTaxesPaymentsQueueItem(row)),
      ...visibleBundledExperienceRows.map((row) => buildBundledExperienceQueueItem(row)),
    ],
    [
      blackoutCoverage,
      visibleBlackoutRows,
      visibleBundledExperienceRows,
      visibleCapacityRows,
      visibleCampaignRows,
      visibleDatesRestrictionsRows,
      visibleMarginRows,
      visibleSetupRows,
      visibleSurchargeRules,
      visibleSurchargeTableRows,
      visibleTaxesPaymentsRows,
    ],
  );
  const reportSummaries = useMemo(
    () =>
      buildReportSummaries({
        blackoutRows,
        bundledExperienceRows,
        taxesPaymentsRows,
        capacityRows,
        campaignRows,
        datesRestrictionsRows,
        manualApprovals,
        marginRows,
        offerId,
        propertyId,
        setupRows,
        vendorId,
      }),
    [
      blackoutRows,
      bundledExperienceRows,
      campaignRows,
      taxesPaymentsRows,
      capacityRows,
      datesRestrictionsRows,
      manualApprovals,
      marginRows,
      offerId,
      propertyId,
      setupRows,
      vendorId,
    ],
  );
  const areVisibleQueueItemsExpanded =
    visibleQueueItems.length > 0 && visibleQueueItems.every((queueItem) => expandedQueueItems[queueItem.id]);
  const summary = useChecklistSummary({
    baseSurchargesRows,
    campaignRows,
    datesRestrictionsRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    manualApprovals,
    marginRows,
    setupRows,
    surchargeTableRows,
  });
  const isLoading = isLoadingProperty || isLoadingSetup || isLoadingSurcharges;
  const hasError = !!propertyError || !!surchargesError;

  if (hasError) {
    return <Alert severity="error">Unable to load the CM checklist data.</Alert>;
  }

  if (isLoading) {
    return <Alert severity="info">Loading CM checklist data.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <ContextCard
        approvedCount={summary.approvedCount}
        bookByDate={bookByDate}
        campaignType={campaignType}
        channelManager={channelManager}
        hotelName={hotelName}
        needsApprovalCount={summary.needsApprovalCount}
        onSelectApprovalQueue={() => setActiveTabId('approvalQueue')}
        offerId={offerId}
        travelToDate={travelToDate}
        totalCount={summary.totalCount}
      />

      <ChecklistToolbar
        activeTabId={activeTabId}
        areVisibleQueueItemsExpanded={areVisibleQueueItemsExpanded}
        isApprovalQueueMode={isApprovalQueueMode}
        needsApprovalCount={summary.needsApprovalCount}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        visibleQueueItemCount={visibleQueueItems.length}
        onExpandVisibleCards={() =>
          setExpandedQueueItems((current) => toggleVisibleQueueItems(current, visibleQueueItems))
        }
        onSearchQueryChange={setSearchQuery}
        onShowAutoApprovedChange={() => setShowAutoApproved((current) => !current)}
        onTabChange={setActiveTabId}
      />

      {isApprovalQueueMode ? (
        <ApprovalQueue
          expandedQueueItems={expandedQueueItems}
          items={visibleQueueItems}
          needsApprovalCount={summary.needsApprovalCount}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onToggleDetails={(itemId) => setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }))}
          reportSummaries={reportSummaries}
        />
      ) : isSetupCategoryActive ? (
        <SetupReportCard
          hiddenApprovedCount={setupRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          inputOverrides={setupInputOverrides}
          manualApprovals={manualApprovals}
          rows={visibleSetupRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onInputOverrideChange={(rowId, inputLabel, value) => {
            setSetupInputOverrides((current) => ({
              ...current,
              [rowId]: {
                ...current[rowId],
                [inputLabel]: value,
              },
            }));
            setManualApprovals((current) => {
              const { [rowId]: _removedApproval, ...nextApprovals } = current;
              return nextApprovals;
            });
          }}
        />
      ) : isCampaignCategoryActive ? (
        <CampaignReportCard
          hiddenApprovedCount={campaignRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleCampaignRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
        />
      ) : isDatesRestrictionsCategoryActive ? (
        <DatesRestrictionsReportCard
          hiddenApprovedCount={datesRestrictionsRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleDatesRestrictionsRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
        />
      ) : isEgsCategoryActive ? (
        <>
          <MarginReportCard
            hiddenApprovedCount={marginRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleMarginRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedMargins((current) => ({
                ...current,
                [rowId]: {
                  costPrice: current[rowId]?.costPrice ?? marginRowsById.get(rowId)?.costPrice,
                  sellPrice: current[rowId]?.sellPrice ?? marginRowsById.get(rowId)?.sellPrice,
                  [fieldName]: value,
                },
              }))
            }
          />

          <CapacityReportCard
            hiddenApprovedCount={capacityRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleCapacityRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) =>
              setEditedCapacities((current) => ({
                ...current,
                [rowId]: {
                  egsCapacity: current[rowId]?.egsCapacity ?? capacityRowsById.get(rowId)?.egsCapacity,
                  includedCapacity: current[rowId]?.includedCapacity ?? capacityRowsById.get(rowId)?.includedCapacity,
                  maxRoomCapacity: current[rowId]?.maxRoomCapacity ?? capacityRowsById.get(rowId)?.maxRoomCapacity,
                  [fieldName]: value,
                },
              }))
            }
          />
        </>
      ) : isSurchargesCategoryActive ? (
        <SurchargesReportCard
          flaggedRules={baseSurchargesRows}
          hiddenApprovedCount={surchargeTableRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleSurchargeTableRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
        />
      ) : isBlackoutCategoryActive ? (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity blackout dates — affected rules require approval.
            </Alert>
          )}
          <BlackoutReportCard
            coverage={blackoutCoverage}
            hiddenApprovedCount={blackoutRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleBlackoutRows}
            showAutoApproved={showAutoApproved}
            travelFrom={opportunityTravelFrom}
            travelTo={opportunityTravelTo}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onValueChange={(rowId, fieldName, value) => {
              setEditedBlackouts((current) => ({
                ...current,
                [rowId]: {
                  from: current[rowId]?.from ?? blackoutRowsById.get(rowId)?.from,
                  name: current[rowId]?.name ?? blackoutRowsById.get(rowId)?.name,
                  to: current[rowId]?.to ?? blackoutRowsById.get(rowId)?.to,
                  [fieldName]: value,
                },
              }));
              setManualApprovals((current) => {
                const { [rowId]: _removedApproval, ...nextApprovals } = current;
                return nextApprovals;
              });
            }}
          />
        </>
      ) : isBundledExperiencesCategoryActive ? (
        <BundledExperiencesReportCard
          hiddenApprovedCount={bundledExperienceRows.filter((row) => isRowApproved(row, manualApprovals)).length}
          manualApprovals={manualApprovals}
          rows={visibleBundledExperienceRows}
          showAutoApproved={showAutoApproved}
          onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
          onValueChange={(rowId, fieldName, value) => {
            setEditedBundledExperiences((current) => ({
              ...current,
              [rowId]: {
                blackoutDateRange:
                  current[rowId]?.blackoutDateRange ?? bundledExperienceRowsById.get(rowId)?.blackoutDateRange,
                bundledExperience:
                  current[rowId]?.bundledExperience ?? bundledExperienceRowsById.get(rowId)?.bundledExperience,
                childAge: current[rowId]?.childAge ?? bundledExperienceRowsById.get(rowId)?.childAge,
                currency: current[rowId]?.currency ?? bundledExperienceRowsById.get(rowId)?.currency,
                travelFrom: current[rowId]?.travelFrom ?? bundledExperienceRowsById.get(rowId)?.travelFrom,
                travelTo: current[rowId]?.travelTo ?? bundledExperienceRowsById.get(rowId)?.travelTo,
                [fieldName]: value,
              },
            }));
            setManualApprovals((current) => {
              const { [rowId]: _removedApproval, ...nextApprovals } = current;
              return nextApprovals;
            });
          }}
        />
      ) : isTaxesPaymentsCategoryActive ? (
        <>
          {opportunityError && (
            <Alert severity="warning">
              Unable to load Salesforce opportunity data — some rules may show as missing data.
            </Alert>
          )}
          <TaxesPaymentsReportCard
            expandedRows={expandedQueueItems}
            hiddenApprovedCount={taxesPaymentsRows.filter((row) => isRowApproved(row, manualApprovals)).length}
            manualApprovals={manualApprovals}
            rows={visibleTaxesPaymentsRows}
            showAutoApproved={showAutoApproved}
            onApprovalChange={(rowId) => setManualApprovals((current) => ({ ...current, [rowId]: true }))}
            onToggleDetails={(itemId) =>
              setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }))
            }
            onUnapprovalChange={(rowId) => setManualApprovals(({ [rowId]: _, ...rest }) => rest)}
          />
        </>
      ) : (
        <PlaceholderChecklistTab category={activeCategory} />
      )}
    </Stack>
  );
}

function applyEditedMarginRows(rows: Array<EgsMarginChecklistRow>, editedMargins: EditedMarginValues) {
  return rows.map((row) => {
    const edited = editedMargins[row.id];
    if (!edited) {
      return row;
    }

    const costPrice = edited.costPrice;
    const sellPrice = edited.sellPrice;
    const margin = calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ...row,
      costPrice,
      margin,
      marginBand,
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function applyEditedCapacityRows(rows: Array<EgsCapacityChecklistRow>, editedCapacities: EditedCapacityValues) {
  return rows.map((row) => {
    const edited = editedCapacities[row.id];
    if (!edited) {
      return row;
    }

    const { egsCapacity, includedCapacity, maxRoomCapacity } = edited;
    const hasAllValues =
      isFiniteNumber(egsCapacity) && isFiniteNumber(includedCapacity) && isFiniteNumber(maxRoomCapacity);
    const status: ChecklistStatus = hasAllValues
      ? maxRoomCapacity === includedCapacity + egsCapacity
        ? 'approved'
        : 'requires approval'
      : 'requires approval';

    return {
      ...row,
      egsCapacity,
      includedCapacity,
      maxRoomCapacity,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function applyEditedBlackoutRows({
  editedBlackouts,
  rows,
  travelFrom,
  travelTo,
}: {
  editedBlackouts: EditedBlackoutValues;
  rows: Array<BlackoutChecklistRow>;
  travelFrom?: string;
  travelTo?: string;
}) {
  return rows.map((row) => {
    const edited = editedBlackouts[row.id];
    if (!edited) {
      return row;
    }

    return evaluateBlackoutRow({
      from: edited.from ?? row.from,
      id: row.id,
      name: edited.name ?? row.name,
      to: edited.to ?? row.to,
      travelFrom,
      travelTo,
    });
  });
}

function applyEditedBundledExperienceRows({
  dealCurrency,
  editedBundledExperiences,
  hotelBlackoutDates,
  hotelTravelFrom,
  hotelTravelTo,
  rows,
}: {
  dealCurrency?: string;
  editedBundledExperiences: EditedBundledExperienceValues;
  hotelBlackoutDates?: App.OpportunityPage['black_out_dates'];
  hotelTravelFrom?: string;
  hotelTravelTo?: string;
  rows: Array<BundledExperienceChecklistRow>;
}) {
  return rows.map((row) => {
    const edited = editedBundledExperiences[row.id];
    if (!edited) {
      return row;
    }

    const blackoutDateRange = edited.blackoutDateRange;
    const bundledExperience = edited.bundledExperience;
    const childAge = edited.childAge;
    const currency = edited.currency;
    const travelFrom = edited.travelFrom;
    const travelTo = edited.travelTo;
    const validityOk = isBundledValidityOk({ hotelTravelFrom, hotelTravelTo, travelFrom, travelTo });
    const blackoutsOk = isBundledBlackoutOk({ blackoutDateRange, hotelBlackoutDates });
    const rowDealCurrency = dealCurrency ?? row.dealCurrency;
    const currencyOk = !!currency && !!rowDealCurrency && currency === rowDealCurrency;
    const childAgeOk = isBundledChildAgeOk(childAge);
    const status: ChecklistStatus =
      validityOk && blackoutsOk && currencyOk && childAgeOk ? 'approved' : 'requires approval';

    return {
      ...row,
      blackoutDateRange,
      blackoutsOk,
      bundledExperience,
      childAge,
      childAgeOk,
      currency,
      currencyOk,
      status,
      statusLabel: getStatusLabel(status),
      travelFrom,
      travelTo,
      validityOk,
    };
  });
}

function applySetupInputOverrides(input: SetupRuleInput, inputOverrides: SetupInputOverrides): SetupRuleInput {
  const nextInput: SetupRuleInput = {
    ...input,
    account: { ...input.account },
    exclusions: input.exclusions ? { ...input.exclusions } : undefined,
    opportunity: { ...input.opportunity },
    property: { ...input.property },
  };

  Object.entries(inputOverrides).forEach(([rowId, values]) => {
    Object.entries(values).forEach(([label, value]) => {
      applySetupInputOverrideValue(nextInput, rowId, label, value);
    });
  });

  return nextInput;
}

function applySetupInputOverrideValue(input: SetupRuleInput, rowId: string, label: string, value: string) {
  switch (`${rowId}:${label}`) {
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.accountName}`:
      input.account = { ...input.account, name: value };
      break;
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.propertyName}`:
      input.property = { ...input.property, name: value };
      break;
    case `setup-account-name:${SETUP_EVIDENCE_LABELS.propertyWebsite}`:
      input.property = { ...input.property, website: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.propertyCountry}`:
      input.property = { ...input.property, country: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abnVerified}`:
      input.account = { ...input.account, abnVerified: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abnStatus}`:
      input.account = { ...input.account, abnStatus: value };
      break;
    case `setup-account-abn:${SETUP_EVIDENCE_LABELS.abn}`:
      input.account = { ...input.account, abn: value };
      break;
    case `setup-account-currency:${SETUP_EVIDENCE_LABELS.hotelAccountCurrency}`:
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.reportingCurrency}`:
      input.account = { ...input.account, bankCurrency: value };
      break;
    case `setup-account-currency:${SETUP_EVIDENCE_LABELS.opportunityCurrency}`:
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.sellCurrency}`:
      input.opportunity = { ...input.opportunity, currencyIsoCode: value };
      break;
    case `setup-campaign-schedules-exclusions:${SETUP_EVIDENCE_LABELS.excludedBrands}`:
      input.exclusions = { ...input.exclusions, brands: value };
      break;
    case `setup-campaign-schedules-exclusions:${SETUP_EVIDENCE_LABELS.excludedCountries}`:
      input.exclusions = { ...input.exclusions, countries: value };
      break;
    case `setup-marketing-terms:${SETUP_EVIDENCE_LABELS.canonicalPropertyChain}`:
      input.chainName = value;
      break;
    case `setup-marketing-terms:${SETUP_EVIDENCE_LABELS.contractTemplateId}`:
    case `setup-contracts-geo:${SETUP_EVIDENCE_LABELS.contractTemplateId}`:
      input.account = { ...input.account, contractTemplateId: value };
      break;
    case `setup-contracts-geo:${SETUP_EVIDENCE_LABELS.leContractsGeo}`:
      input.opportunity = { ...input.opportunity, geo: value };
      break;
    case `setup-sell-cost-reporting-currency:${SETUP_EVIDENCE_LABELS.costCurrency}`:
      input.opportunity = { ...input.opportunity, costPriceCurrency: value };
      break;
    default:
      break;
  }
}

function useChecklistSummary({
  baseSurchargesRows,
  campaignRows,
  datesRestrictionsRows,
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  manualApprovals,
  marginRows,
  setupRows,
  surchargeTableRows,
}: {
  baseSurchargesRows: Array<SurchargesChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  datesRestrictionsRows: Array<DatesRestrictionsChecklistRow>;
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  setupRows: Array<SetupChecklistRow>;
  surchargeTableRows: Array<SurchargeTableRow>;
}) {
  return useMemo(() => {
    const allRows = [
      ...setupRows,
      ...campaignRows,
      ...datesRestrictionsRows,
      ...bundledExperienceRows,
      ...marginRows,
      ...capacityRows,
      ...blackoutRows,
      ...taxesPaymentsRows,
      ...baseSurchargesRows,
      ...surchargeTableRows,
    ];
    const approvedCount = allRows.filter((row) => isRowApproved(row, manualApprovals)).length;
    const blockedCount = allRows.filter((row) => row.status === 'blocked').length;
    const totalCount = allRows.length;
    const needsApprovalCount = totalCount - approvedCount - blockedCount;

    return {
      approvedCount,
      needsApprovalCount,
      totalCount,
    };
  }, [
    baseSurchargesRows,
    campaignRows,
    datesRestrictionsRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    manualApprovals,
    marginRows,
    setupRows,
    surchargeTableRows,
  ]);
}

function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

function buildMarginQueueItem(row: EgsMarginChecklistRow): ApprovalQueueItem {
  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} has a ${row.marginBand ? `${row.marginBand} ` : ''}margin check and needs review.`,
    details: [
      { label: 'Opportunity', value: row.opportunityName || 'Opportunity not mapped' },
      { label: 'Extra guest type', value: row.extraGuestType || 'N/A' },
      { label: 'Age range', value: row.ageRange || 'N/A' },
      { label: 'Cost price', value: formatMoney(row.costPrice, row.currencyCode) },
      { label: 'Sell price', value: formatMoney(row.sellPrice, row.currencyCode) },
      { label: 'Margin', value: typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A' },
      { label: 'Bedding type', value: row.beddingType || 'N/A' },
      { label: 'Inclusions', value: row.inclusions || 'N/A' },
      { label: 'Collected by', value: row.collectedBy || 'N/A' },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildCapacityQueueItem(row: EgsCapacityChecklistRow): ApprovalQueueItem {
  const includedPlusEgs =
    isFiniteNumber(row.includedCapacity) && isFiniteNumber(row.egsCapacity)
      ? String(row.includedCapacity + row.egsCapacity)
      : 'N/A';

  return {
    categoryId: EGS_CATEGORY.id,
    categoryLabel: EGS_CATEGORY.label,
    description: `${row.name} capacity requires approval.`,
    details: [
      { label: 'Room asset', value: row.roomName },
      { label: 'Included capacity', value: formatOptionalNumber(row.includedCapacity) },
      { label: 'Max room capacity', value: formatOptionalNumber(row.maxRoomCapacity) },
      { label: 'EGS contracted for', value: formatOptionalNumber(row.egsCapacity) },
      { label: 'Included + EGS', value: includedPlusEgs },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildCampaignQueueItem(row: CampaignChecklistRow): ApprovalQueueItem {
  return {
    categoryId: CAMPAIGN_CATEGORY.id,
    categoryLabel: CAMPAIGN_CATEGORY.label,
    description: `${row.field}${row.packageName ? ` for ${row.packageName}` : ''} requires approval.`,
    details: [
      { label: 'Value', value: row.value },
      { label: 'Rule', value: row.rule },
      { label: 'Evidence', value: row.evidence },
      { label: 'Source', value: row.source },
      ...(row.action ? [{ label: 'Action', value: row.action }] : []),
    ],
    id: row.id,
    title: row.field,
  };
}

function buildDatesRestrictionsQueueItem(row: DatesRestrictionsChecklistRow): ApprovalQueueItem {
  return {
    categoryId: DATES_RESTRICTIONS_CATEGORY.id,
    categoryLabel: DATES_RESTRICTIONS_CATEGORY.label,
    description: `${row.title} requires approval.`,
    details: [{ label: 'Rule', value: row.rule }, { label: 'Source value', value: row.sourceValue }, ...row.details],
    id: row.id,
    title: row.title,
  };
}

function buildSurchargesQueueItem(row: SurchargesChecklistRow): ApprovalQueueItem {
  return {
    categoryId: SURCHARGES_CATEGORY.id,
    categoryLabel: SURCHARGES_CATEGORY.label,
    description: `${row.name} surcharge rule requires review.`,
    details: row.details,
    id: row.id,
    title: row.name,
  };
}

function buildSurchargeTableQueueItem(row: SurchargeTableRow): ApprovalQueueItem {
  return {
    categoryId: SURCHARGES_CATEGORY.id,
    categoryLabel: SURCHARGES_CATEGORY.label,
    description: `${row.name} surcharge requires review.`,
    details: [
      { label: 'Period', value: `${formatContextDate(row.fromDate)} – ${formatContextDate(row.toDate)}` },
      { label: 'Converted Cost', value: formatMoney(row.convertedCost, row.currencyCode) },
      { label: 'Surcharge Price', value: formatMoney(row.surchargePrice, row.currencyCode) },
      { label: 'Accumulate', value: row.accumulate ? 'Yes' : 'No' },
      {
        label: 'Inside Travel Period',
        value: row.insideTravelPeriod === null ? 'N/A' : row.insideTravelPeriod ? 'Yes' : 'No',
      },
      {
        label: 'Blackout Overlap',
        value: row.blackoutOverlap === null ? 'N/A' : row.blackoutOverlap ? 'Yes' : 'No',
      },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildBlackoutQueueItem(row: BlackoutChecklistRow, coverage: BlackoutCoverageSummary): ApprovalQueueItem {
  return {
    categoryId: BLACKOUT_CATEGORY.id,
    categoryLabel: BLACKOUT_CATEGORY.label,
    description: `${row.name} blackout dates require approval.`,
    details: [
      { label: 'From', value: formatContextDate(row.from) },
      { label: 'To', value: formatContextDate(row.to) },
      { label: 'Within travel validity', value: row.withinTravelValidity ? 'Yes' : 'No' },
      { label: 'Clash with gala / festive dates', value: row.festiveClear ? 'No' : 'Yes' },
      { label: 'Total blackout coverage', value: `${coverage.blackoutDays} days (${coverage.percent}%)` },
    ],
    id: row.id,
    title: row.name,
  };
}

function buildSetupQueueItem(row: SetupChecklistRow): ApprovalQueueItem {
  return {
    categoryId: SETUP_CATEGORY.id,
    categoryLabel: SETUP_CATEGORY.label,
    description: row.summary,
    details: row.evidence.map((evidence) => ({ label: evidence.label, value: evidence.value })),
    id: row.id,
    title: row.field,
  };
}

function buildBundledExperienceQueueItem(row: BundledExperienceChecklistRow): ApprovalQueueItem {
  return {
    categoryId: BUNDLED_EXPERIENCES_CATEGORY.id,
    categoryLabel: BUNDLED_EXPERIENCES_CATEGORY.label,
    description: `${row.bundledExperience} needs bundled-experience review.`,
    details: [
      { label: 'Linked deal options', value: row.linkedDealOptions.join(', ') || 'N/A' },
      { label: 'Validity from', value: row.travelFrom || 'N/A' },
      { label: 'Validity to', value: row.travelTo || 'N/A' },
      { label: 'Blackouts', value: row.blackoutDateRange || 'None' },
      { label: 'Currency', value: row.currency || 'N/A' },
      { label: 'Children age', value: row.childAge || 'N/A' },
    ],
    id: row.id,
    title: row.bundledExperience,
  };
}

function buildReportSummaries({
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  campaignRows,
  datesRestrictionsRows,
  manualApprovals,
  marginRows,
  offerId,
  propertyId,
  setupRows,
  vendorId,
}: {
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  datesRestrictionsRows: Array<DatesRestrictionsChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  offerId: string;
  propertyId?: string;
  setupRows: Array<SetupChecklistRow>;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const summaries: Array<ApprovalReportSummary> = [];

  const setupRowsRequiringApproval = setupRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (setupRowsRequiringApproval.length > 0) {
    const setupRowCopy = `setup row${setupRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const setupReviewVerb = setupRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: SETUP_CATEGORY.id,
      categoryLabel: SETUP_CATEGORY.label,
      count: setupRowsRequiringApproval.length,
      description: `${setupRowsRequiringApproval.length} ${setupRowCopy} still ${setupReviewVerb} review. Verify setup evidence before approval.`,
      sourceLinks: [],
    });
  }

  const campaignRowsRequiringApproval = campaignRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (campaignRowsRequiringApproval.length > 0) {
    const campaignRowCopy = `Campaign row${campaignRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const campaignReviewVerb = campaignRowsRequiringApproval.length === 1 ? 'needs' : 'need';

    summaries.push({
      categoryId: CAMPAIGN_CATEGORY.id,
      categoryLabel: CAMPAIGN_CATEGORY.label,
      count: campaignRowsRequiringApproval.length,
      description: `${campaignRowsRequiringApproval.length} ${campaignRowCopy} still ${campaignReviewVerb} review. Verify source values in offer details, opportunity details, and the Policy Bible.`,
      sourceLinks: [
        {
          href: 'https://aussiecommerce.atlassian.net/wiki/spaces/SO/pages/2335834126/Contracting+and+Assessment+Information+Chains+Hotel+Specific',
          label: 'Open Policy Bible',
        },
      ],
    });
  }

  const datesRestrictionsRowsRequiringApproval = datesRestrictionsRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (datesRestrictionsRowsRequiringApproval.length > 0) {
    const rowCopy = `Dates & Restrictions row${datesRestrictionsRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const reviewVerb = datesRestrictionsRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: DATES_RESTRICTIONS_CATEGORY.id,
      categoryLabel: DATES_RESTRICTIONS_CATEGORY.label,
      count: datesRestrictionsRowsRequiringApproval.length,
      description: `${datesRestrictionsRowsRequiringApproval.length} ${rowCopy} still ${reviewVerb} review. Verify deal-option setup and synced Salesforce values.`,
      sourceLinks: [],
    });
  }

  const blackoutRowsRequiringApproval = blackoutRows.filter((row) => !isRowApproved(row, manualApprovals));
  const blackoutReviewCount = blackoutRowsRequiringApproval.length;
  if (blackoutReviewCount > 0) {
    const blackoutRuleCopy = `blackout rule${blackoutReviewCount === 1 ? '' : 's'}`;
    const blackoutReviewVerb = blackoutReviewCount === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: BLACKOUT_CATEGORY.id,
      categoryLabel: BLACKOUT_CATEGORY.label,
      count: blackoutReviewCount,
      description: `${blackoutReviewCount} ${blackoutRuleCopy} still ${blackoutReviewVerb} review. Verify blackout dates against travel validity and gala/festive dates.`,
      sourceLinks: [],
    });
  }

  const egsRowsRequiringApproval = [...marginRows, ...capacityRows].filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (egsRowsRequiringApproval.length > 0) {
    const egsRowCopy = `EGS row${egsRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const egsReviewVerb = egsRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${egsRowsRequiringApproval.length} ${egsRowCopy} still ${egsReviewVerb} review. Verify source data in AP room-rate capacity and surcharge setup.`,
      sourceLinks: buildEgsSourceLinks({ capacityRows, propertyId, vendorId }),
    });
  }

  const bundledExperienceRowsRequiringApproval = bundledExperienceRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (bundledExperienceRowsRequiringApproval.length > 0) {
    const rowCopy = `bundled experience row${bundledExperienceRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const reviewVerb = bundledExperienceRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: BUNDLED_EXPERIENCES_CATEGORY.id,
      categoryLabel: BUNDLED_EXPERIENCES_CATEGORY.label,
      count: bundledExperienceRowsRequiringApproval.length,
      description: `${bundledExperienceRowsRequiringApproval.length} ${rowCopy} still ${reviewVerb} review. Verify synced deal-option and opportunity evidence in Salesforce.`,
      sourceLinks: [{ href: `/opportunity/${offerId}`, label: 'Verify source data' }],
    });
  }

  const taxesRowsRequiringApproval = taxesPaymentsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (taxesRowsRequiringApproval.length > 0) {
    const taxesRowCopy = `rule${taxesRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const taxesReviewVerb = taxesRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: TAXES_PAYMENTS_CATEGORY.id,
      categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
      count: taxesRowsRequiringApproval.length,
      description: `${taxesRowsRequiringApproval.length} Taxes & Payments ${taxesRowCopy} still ${taxesReviewVerb} review. Verify source data in the Salesforce opportunity contract and Admin Portal property taxes.`,
      sourceLinks: [],
    });
  }

  return summaries;
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}

function getChecklistCategory(categoryId: ChecklistCategoryId): ChecklistCategory {
  return CM_CHECKLIST_CATEGORIES.find((category) => category.id === categoryId) ?? CM_CHECKLIST_CATEGORIES[0];
}

function toggleVisibleQueueItems(
  current: Record<string, boolean>,
  visibleQueueItems: Array<ApprovalQueueItem>,
): Record<string, boolean> {
  const shouldExpand = !visibleQueueItems.every((queueItem) => current[queueItem.id]);

  return visibleQueueItems.reduce(
    (next, queueItem) => ({
      ...next,
      [queueItem.id]: shouldExpand,
    }),
    current,
  );
}

function isRowApproved(row: ChecklistRow, manualApprovals: ManualApprovalState) {
  return row.status === 'approved' || !!manualApprovals[row.id];
}

function buildTaxesPaymentsQueueItem(row: TaxesPaymentsChecklistRow): ApprovalQueueItem {
  return {
    categoryId: TAXES_PAYMENTS_CATEGORY.id,
    categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
    description: `${row.name} requires review.`,
    details: row.details,
    id: row.id,
    title: row.name,
  };
}

function getMarginSearchText(row: EgsMarginChecklistRow) {
  return [
    row.name,
    row.opportunityName,
    row.extraGuestType,
    row.ageRange,
    row.currencyCode,
    row.beddingType,
    row.inclusions,
    row.collectedBy,
    row.statusLabel,
  ]
    .filter(Boolean)
    .join(' ');
}

function getCapacitySearchText(row: EgsCapacityChecklistRow) {
  return [row.name, row.roomName, row.statusLabel].filter(Boolean).join(' ');
}

function getCampaignSearchText(row: CampaignChecklistRow) {
  return [row.field, row.packageName, row.value, row.rule, row.evidence, row.source, row.statusLabel, row.action]
    .filter(Boolean)
    .join(' ');
}

function getDatesRestrictionsSearchText(row: DatesRestrictionsChecklistRow) {
  return [row.title, row.rule, row.sourceValue, row.statusLabel].filter(Boolean).join(' ');
}

function getSurchargesSearchText(row: SurchargesChecklistRow) {
  return [row.name, row.field, row.statusLabel].join(' ');
}

function getSurchargeTableSearchText(row: SurchargeTableRow) {
  return [row.name, row.statusLabel].join(' ');
}

function getBlackoutSearchText(row: BlackoutChecklistRow) {
  return [row.name, row.field, row.statusLabel, row.from, row.to].filter(Boolean).join(' ');
}

function getSetupSearchText(row: SetupChecklistRow) {
  return [row.field, row.rule, row.summary, row.statusLabel, ...row.evidence.map((evidence) => evidence.value)]
    .filter(Boolean)
    .join(' ');
}

function getTaxesPaymentsSearchText(row: TaxesPaymentsChecklistRow) {
  return [row.name, row.field, row.statusLabel].filter(Boolean).join(' ');
}

function getBundledExperienceSearchText(row: BundledExperienceChecklistRow) {
  return [
    row.bundledExperience,
    row.linkedDealOptions.join(' '),
    row.blackoutDateRange,
    row.currency,
    row.childAge,
    row.statusLabel,
  ]
    .filter(Boolean)
    .join(' ');
}

function getOfferCurrency(packages?: Array<App.Package>) {
  return packages?.find((offerPackage) => offerPackage.cost_currency)?.cost_currency;
}
